// oxlint-disable no-unused-vars
import { Box, lighten, Button, Autocomplete, TextField, Checkbox, Stack, Typography, Tooltip } from "@mui/material";
import {
	MRT_RowSelectionState,
	MRT_VisibilityState,
	MRT_GroupingState,
	MRT_ColumnOrderState,
	MRT_ColumnDef,
	useMaterialReactTable,
	MRT_GlobalFilterTextField,
	MRT_ToggleFiltersButton,
	MaterialReactTable,
	MRT_ExpandedState,
	MRT_ExpandAllButton,
	MRT_PaginationState,
	MRT_ToggleFullScreenButton,
	MRT_Row,
	MRT_SelectCheckbox,
} from "material-react-table";
import { useState, useMemo, useEffect } from "react";
import { GeoFileType, getJobPriorities, JobFlags } from "../../../../utilities/types";
import { toast } from "react-toastify";
import { API } from "../../../../api";
import type { Deal, Job } from "../../../../models";
import { Oops } from "../../../../utilities/oops";
import { AutoAwesome, Download, Publish, Map } from "@mui/icons-material";
import { FileDownloader } from "../../../../utilities/downloadFile";
import JSZip from "jszip";
import type { User } from "../../../../models";
import { FeatureCollection, Point, Polygon, MultiPolygon } from "geojson";
import { GeoData } from "@rogoag/shared-ts";
import { getDealLabel } from "../../../../utilities/utils";

export interface PropertyWithExamples {
	name: string;
	examples: string[];
}

export interface JobsImportTableCSVProps {
	jobs: Job[];
	updateJobs: (jobs: Job[]) => void;
	deals: Deal[];
	setDrawerState: (open: boolean) => void;
	setLoading: (task: string | boolean) => void;
	resetForm: () => void;
	allDeals: Deal[];
	allOptions: Record<GeoFileType, File[]>;
	integration?: IntegrationType;
	user?: User;
	shouldShowAutoMatch?: boolean;
	// Map-related props
	setDisplayBoundaries?: (
		boundaries: Record<string, FeatureCollection<Polygon> | FeatureCollection<MultiPolygon>>,
	) => void;
	setDisplayedZones?: (zones: Record<string, FeatureCollection<Polygon> | FeatureCollection<MultiPolygon>>) => void;
	setDisplayedPoints?: (points: Record<string, FeatureCollection<Point>>) => void;
	drawerOpen?: boolean;
	allowMap?: boolean;
	showHeaderSelectors?: boolean;
	headerProperties?: PropertyWithExamples[];
}

export type IntegrationType = "Proagrica" | "Agrian";

// OLD version. Set to be removed after BUv2 is working
export default function JobImportTableCSV(props: JobsImportTableCSVProps) {
	const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
	const [selectedJobs, setSelectedJobs] = useState<Job[]>([]);
	const [selectionDisplayedOnMap, setSelectionDisplayedOnMap] = useState<MRT_RowSelectionState>({});
	const [expanded, setExpanded] = useState<MRT_ExpandedState>(true);
	const [jobs, setJobs] = useState<Job[]>(props.jobs);
	const [allShapefiles, setAllShapefiles] = useState<Record<GeoFileType, File[]>>({ ...props.allOptions });
	const [availableShapefiles, setAvailableShapefiles] = useState<Record<GeoFileType, File[]>>({ ...props.allOptions });
	const [selectedShapefiles, setSelectedShapefiles] = useState<Record<GeoFileType, File[]>>({
		Boundary: [],
		Centroids: [],
		Cores: [],
		Zones: [],
		Unknown: [],
	});
	const [readySelectionState, setReadySelectionState] = useState<Record<string, boolean>>({});
	const [recordBoundaryState, setRecordBoundaryState] = useState<Record<string, boolean>>({});
	const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 0,
		pageSize: 10,
	});
	const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>({
		// 'farm': false,
	});
	const [grouping, setGrouping] = useState<MRT_GroupingState>(
		props.jobs.filter((job) => !!job.importGrowerName).length > 0 ? ["grower"] : [],
	);
	const [columnOrder, setColumnOrder] = useState<MRT_ColumnOrderState>(["grower", "field", "farm"]);
	// these are the potential options for the autocomplete fields
	const [gridSizes] = useState<number[]>([
		...new Set([
			...props.allDeals.flatMap((deal) => {
				return deal.density;
			}),
			...props.jobs.flatMap((job) => (job.importDensity ? [job.importDensity] : [])),
		]),
	]);
	const [depths] = useState<number[]>([
		...new Set([
			...props.allDeals.flatMap((deal) => {
				return deal.depth;
			}),
			...props.jobs.flatMap((job) => (job.importDepth ? [job.importDepth] : [])),
		]),
	]);
	const [labPackages] = useState<string[]>([
		...new Set<string>([
			...props.allDeals.flatMap((deal) => {
				const field = deal.testPackage;
				return field !== undefined && field !== "" ? field : [];
			}),
			...props.jobs.flatMap((job) => (job.externalTestPackageId ? [job.externalTestPackageId] : [])),
			...["No test package specified"],
		]),
	]);
	const [micros] = useState<number[]>([
		...new Set(
			props.allDeals.flatMap((deal) => {
				return deal.frequencyOfMicros ?? 0;
			}),
		),
		...props.jobs.flatMap((job) => (job.externalFrequencyOfMicros ? [job.externalFrequencyOfMicros] : [])),
	]);

	const checkErrors = (jobs: Job[]) => {
		for (const job of jobs) {
			if (job.importDensity == undefined || job.importDensity == 0 || isNaN(parseFloat(job.importDensity.toString()))) {
				setValidationErrors((prev) => ({ ...prev, [job.id]: "Density must be a number" }));
			} else if (
				job.importDepth == undefined ||
				job.importDepth == 0 ||
				isNaN(parseFloat(job.importDepth.toString()))
			) {
				setValidationErrors((prev) => ({ ...prev, [job.id]: "Depth must be a number" }));
			} else {
				setValidationErrors((prev) => {
					const newErrors = { ...prev };
					delete newErrors[job.id];
					return newErrors;
				});
			}
		}
	};

	const checkRequiredFields = (jobs: Job[]) => {
		for (const job of jobs) {
			// Check grower name
			if (!job.importGrowerName || job.importGrowerName.trim() === "") {
				setValidationErrors((prev) => ({ ...prev, [`grower-${job.id}`]: "Grower name is required" }));
			}

			// Check farm name
			if (!job.importFarmName || job.importFarmName.trim() === "") {
				setValidationErrors((prev) => ({ ...prev, [`farm-${job.id}`]: "Farm name is required" }));
			}

			// Check field name
			if (!job.importFieldName || job.importFieldName.trim() === "") {
				setValidationErrors((prev) => ({ ...prev, [`field-${job.id}`]: "Field name is required" }));
			}
		}
	};

	const requiredFieldProps = (fieldType: "grower" | "farm" | "field", fieldKey: keyof Job, errorMessage: string) => {
		return ({ row }: { row: any }) => {
			const validationKey = `${fieldType}-${row.original.id}`;
			return {
				type: "text",
				required: true,
				error: !!validationErrors?.[validationKey],
				helperText: validationErrors?.[validationKey],
				onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
					const value = event.target.value;
					const newJobs = [...jobs];
					const newJob = newJobs.find((j) => j.id === row.original.id);
					if (!newJob) return;
					const index = newJobs.indexOf(newJob);
					(newJob as any)[fieldKey] = value;
					newJobs[index] = newJob;
					setJobs(newJobs);

					// Validate required field
					if (!value || value.trim() === "") {
						setValidationErrors((prev) => ({ ...prev, [validationKey]: errorMessage }));
					} else {
						setValidationErrors((prev) => {
							const newErrors = { ...prev };
							delete newErrors[validationKey];
							return newErrors;
						});
					}
				},
			};
		};
	};

	const findDeal = (deals: Deal[], density: number, depth: number, labPackage: string, micros: number | string) => {
		return deals.find((deal: Deal) => {
			const dealLabPackage = deal.testPackage;
			micros = parseFloat(micros.toString());
			return (
				deal.density == density &&
				deal.depth == depth &&
				deal.frequencyOfMicros == micros &&
				(dealLabPackage == labPackage ||
					((labPackage == "" || labPackage == "No test package specified") &&
						(dealLabPackage == "" || dealLabPackage == undefined)))
			);
		});
	};

	// Map-related functions (similar to JobsDashboardTable)
	async function updateBoundaries(currentRowSelection?: MRT_RowSelectionState) {
		if (!props.setDisplayBoundaries || !props.setDisplayedZones || !props.setDisplayedPoints) {
			console.warn("Map props not provided, skipping boundary update");
			return [0, 0, 0] as const;
		}

		try {
			// Use passed selection or current state
			const selectionToUse = currentRowSelection || rowSelection;
			const selectedJobs = Object.keys(selectionToUse)
				.map((key) => {
					const index = parseInt(key);
					if (!isNaN(index) && selectionToUse[key]) {
						return jobs[index];
					}
				})
				.filter(Boolean) as Job[];

			const displayedBoundaries: Record<string, FeatureCollection<Polygon> | FeatureCollection<MultiPolygon>> = {};
			const displayedPoints: Record<string, FeatureCollection<Point>> = {};
			const displayedZones: Record<string, FeatureCollection<Polygon> | FeatureCollection<MultiPolygon>> = {};

			await Promise.all(
				selectedJobs.map(async (job) => {
					if (!job) {
						return;
					}

					const boundary = job.files.boundarySHP
						? ((await GeoData.toGeoJSON(job.files.boundarySHP, "SHP")) as
								| FeatureCollection<Polygon>
								| FeatureCollection<MultiPolygon>)
						: undefined;
					const points = job.files.pointsSHP
						? ((await GeoData.toGeoJSON(job.files.pointsSHP, "SHP")) as FeatureCollection<Point>)
						: undefined;
					const zones = job.files.zonesSHP
						? ((await GeoData.toGeoJSON(job.files.zonesSHP, "SHP")) as
								| FeatureCollection<Polygon>
								| FeatureCollection<MultiPolygon>)
						: undefined;

					if (boundary) {
						displayedBoundaries[job.id] = boundary;
					}
					if (points) {
						displayedPoints[job.id] = points;
					}
					if (zones) {
						displayedZones[job.id] = zones;
					}
				}),
			);

			props.setDisplayBoundaries(displayedBoundaries);
			props.setDisplayedPoints(displayedPoints);
			props.setDisplayedZones(displayedZones);
			setSelectionDisplayedOnMap(selectionToUse);
			return [
				Object.keys(displayedBoundaries).length,
				Object.keys(displayedPoints).length,
				Object.keys(displayedZones).length,
			] as const;
		} catch (error) {
			Oops.handleError(error, "ImportTable.updateBoundaries");
			return [0, 0, 0] as const;
		}
	}

	async function showOnMap() {
		const loadingBoundariesToast = toast.loading("Updating map...");
		const [boundaryCount, pointCount, zoneCount] = await updateBoundaries();
		toast.dismiss(loadingBoundariesToast);
		toast.info(`Displaying ${boundaryCount} boundaries, ${pointCount} point groups and ${zoneCount} zones`);
	}

	function getShowMapText(): React.ReactNode {
		return selectionDisplayedOnMap !== rowSelection && props.drawerOpen ? "Update Map" : "Show on Map";
	}

	useEffect(() => {
		if (props.allOptions.Boundary) {
			props.allOptions.Boundary.sort((file1, file2) => file1.name.localeCompare(file2.name));
		}
		if (props.allOptions.Centroids) {
			props.allOptions.Centroids.sort((file1, file2) => file1.name.localeCompare(file2.name));
		}
		if (props.allOptions.Zones) {
			props.allOptions.Zones.sort((file1, file2) => file1.name.localeCompare(file2.name));
		}

		setAllShapefiles({ ...props.allOptions });

		const unselectedShapefiles: Record<GeoFileType, File[]> = {} as any;
		(Object.keys(props.allOptions) as GeoFileType[]).forEach((key) => {
			unselectedShapefiles[key] = (props.allOptions[key] || []).filter(
				(file) => !selectedShapefiles[key].some((sel) => sel.name === file.name),
			);
		});
		setAvailableShapefiles(unselectedShapefiles);
	}, [props.allOptions]);

	useEffect(() => {
		// finds matching deals for jobs
		const updatedJobs = props.jobs.map((job) => ({
			...job,
			deal: (
				findDeal(
					props.allDeals,
					job.importDensity ?? 0,
					job.importDepth ?? 0,
					job.externalTestPackageId ?? "",
					job.externalFrequencyOfMicros ?? 0,
				)?.id || "custom"
			).toString(),
		}));

		setJobs(updatedJobs);

		// Check required fields on initial load
		checkRequiredFields(updatedJobs);
	}, [props.jobs, props.deals]);

	// Sync selectedJobs with rowSelection
	useEffect(() => {
		const newSelectedJobs = Object.keys(rowSelection)
			.map((key) => {
				const index = parseInt(key);
				if (!isNaN(index) && rowSelection[key] && jobs[index]) {
					return jobs[index];
				}
			})
			.filter(Boolean) as Job[];

		setSelectedJobs(newSelectedJobs);
	}, [rowSelection, jobs]);

	//#region COLUMNS
	const columns = useMemo<MRT_ColumnDef<Job>[]>(
		() => [
			column_Grower(),
			column_Farm(),
			column_FieldName(),
			column_IsReadyToSample(),
			column_Deal(),
			column_Density(),
			column_Depth(),
			column_TestPackage(),
			column_FrequencyOfMicros(),
			column_FieldPriority(),
			column_BoundarySHP(),
			column_PointsSHP(),
			column_ZonesSHP(),
			column_LabAccountNumber(),
			column_LabSubmissionCode(),
			column_GrowerPhoneNumber(),
			column_SubmissionNotes(),
			column_FieldId(),
		],
		[availableShapefiles, props.jobs, validationErrors, readySelectionState, recordBoundaryState],
	);

	function column_GrowerPhoneNumber(): MRT_ColumnDef<Job, unknown> {
		return {
			id: "growerPhoneNumber",
			header: "Grower Cell",
			accessorKey: "growerPhoneNumber",
			enableEditing: true,
			muiEditTextFieldProps: ({ cell, row }) => ({
				type: "text",
				error: !!validationErrors?.[cell.id],
				helperText: validationErrors?.[cell.id],
				onBlur: (event) => {
					const value = event.target.value;
					if (
						value != "" &&
						!value.match(
							/^\+?(?=.*\d.*\d.*\d.*\d.*\d.*\d.*\d.*\d.*\d.*\d)(?=.*\d?.*\d?.*\d?.*\d?.*\d?.*\d?.*\d?.*\d?.*\d?.*\d?.*\d?).*$/,
						)
					) {
						setValidationErrors((prev) => ({ ...prev, [cell.id]: "Invalid phone number" }));
						return;
					} else {
						setValidationErrors((prev) => {
							const newErrors = { ...prev };
							delete newErrors[cell.id];
							return newErrors;
						});
					}
				},
			}),
		};
	}

	function column_FieldPriority(): MRT_ColumnDef<Job, unknown> {
		return {
			id: "fieldPriority",
			header: "Priority",
			accessorKey: "fieldPriority",
			minSize: 250,
			enableEditing: false,
			Cell: ({ cell, row }) => {
				return (
					<Autocomplete
						size="small"
						style={{ width: "100%", padding: 0 }}
						defaultValue={row.original.fieldPriority}
						options={getJobPriorities()}
						renderInput={(params) => <TextField style={{ padding: 0 }} {...params} />}
						onChange={(event, newValue) => {
							const newJobs = [...jobs];
							const newJob = newJobs.find((j) => {
								return j.id === row.original.id;
							});
							if (!newJob) return;
							const index = newJobs.indexOf(newJob);
							newJob.fieldPriority = newValue || "Regular Turn (default)";
							newJobs[index] = newJob;
							setJobs(newJobs);
						}}
						getOptionKey={(option) => option || ""}
						getOptionLabel={(event) => {
							return event;
						}}
					/>
				);
			},
		};
	}

	function column_LabAccountNumber(): MRT_ColumnDef<Job, unknown> {
		return {
			id: "labAccountNumber",
			header: "Lab Account",
			accessorKey: "labAccountNumber",
			enableEditing: true,
			size: 140,
			muiEditTextFieldProps: ({ cell, row }) => ({
				type: "text",
				required: true,
				error: !!validationErrors?.[cell.id],
				helperText: validationErrors?.[cell.id],
				defaultValue: row.original.labAccountNumber,
				onChange: (event) => {
					const newJobs = [...jobs];
					const newJob = newJobs.find((j) => {
						return j.fieldId === row.original.fieldId;
					});
					if (!newJob) return;
					const index = newJobs.indexOf(newJob);
					// newJob.labAccountNumber = event.target.value;
					newJobs[index] = newJob;
					setJobs(newJobs);
				},
			}),
		};
	}

	function column_IsReadyToSample(): MRT_ColumnDef<Job, unknown> {
		return {
			id: "isReadytoSample",
			header: "Ready?",
			minSize: 100,
			size: 100,
			accessorKey: "isReadytoSample",
			enableEditing: false,
			Cell: ({ cell, row }) => {
				row.original.isReadyToSample = jobs.find((f) => f.id === row.original.id)?.isReadyToSample || false;
				return (
					<Checkbox
						checked={row.original.isReadyToSample}
						onChange={(event) => {
							const newJobs = [...jobs];
							const newJob = newJobs.find((j) => j.id === row.original.id);
							if (!newJob) return;
							const index = newJobs.indexOf(newJob);
							newJob.isReadyToSample = event.target.checked;
							newJobs[index] = newJob;
							setJobs(newJobs);

							const importGrowerName = row.original.importGrowerName ?? "";
							const allAreSelected = newJobs
								.filter((j) => j.importGrowerName === importGrowerName)
								.every((j) => j.isReadyToSample);
							setReadySelectionState((prevState) => ({
								...prevState,
								[importGrowerName]: allAreSelected,
							}));
						}}
					/>
				);
			},
			AggregatedCell: ({ row }) => {
				const importGrowerName = row.original.importGrowerName ?? "";
				return (
					<Checkbox
						checked={readySelectionState[importGrowerName] || false}
						indeterminate={
							!(readySelectionState[importGrowerName] || false) &&
							jobs.some((job) => job.importGrowerName === importGrowerName && job.isReadyToSample)
						}
						onChange={(event) => {
							const isChecked = event.target.checked;
							const newJobs = jobs.map((job) =>
								job.importGrowerName === importGrowerName ? { ...job, isReadyToSample: isChecked } : job,
							);
							setJobs(newJobs);

							setReadySelectionState((prevState) => ({
								...prevState,
								[importGrowerName]: isChecked,
							}));
						}}
					/>
				);
			},
		};
	}

	type ShapefileKey = "boundarySHP" | "pointsSHP" | "zonesSHP";

	function shapefileColumn(
		id: "boundary" | "points" | "zones",
		header: string,
		fileType: keyof typeof availableShapefiles,
	): MRT_ColumnDef<Job, unknown> {
		const fileKeyMap: Record<"boundary" | "points" | "zones", ShapefileKey> = {
			boundary: "boundarySHP",
			points: "pointsSHP",
			zones: "zonesSHP",
		};
		const fileKey = fileKeyMap[id];

		return {
			id,
			header,
			accessorFn: (row) => row.files[fileKey] || [],
			enableEditing: false,
			minSize: 300,
			Cell: ({ cell, row }) => {
				return (
					<Autocomplete
						size="small"
						style={{ width: "100%", padding: 0 }}
						options={availableShapefiles[fileType] || []}
						renderInput={(params) => <TextField style={{ padding: 0 }} {...params} />}
						//@ts-ignore
						groupBy={(option) => option.geoType || ""}
						value={(() => {
							const files = row.original.files[fileKey];
							if (!files?.length) return null;
							const firstFile = files[0];
							// Ensure the file is a valid object with properties, not an empty object
							if (!firstFile || typeof firstFile !== "object" || Object.keys(firstFile).length === 0) {
								return null;
							}
							return firstFile;
						})()}
						onChange={(_, newFile) => {
							const newJobs = [...jobs];
							const newJob = newJobs.find((job) => job.id === row.original.id);
							if (!newJob) return;
							const index = newJobs.indexOf(newJob);

							const oldFile = newJob.files[fileKey]?.length ? newJob.files[fileKey]![0] : null;
							unselectShapefile(oldFile, fileType);
							selectShapefile(newFile, fileType);

							newJob.files[fileKey] = newFile ? [newFile] : [];
							availableShapefiles[fileType].sort((a, b) => a.name.localeCompare(b.name));
							setAvailableShapefiles({ ...availableShapefiles });
							newJobs[index] = newJob;
							setJobs(newJobs);
						}}
						getOptionLabel={(file) => file?.name || ""}
					/>
				);
			},
		};
	}

	function unselectShapefile(oldFile: File | null, fileType: keyof typeof availableShapefiles) {
		if (oldFile) {
			availableShapefiles[fileType].push(oldFile);
			const idx = selectedShapefiles[fileType].indexOf(oldFile);
			if (idx >= 0) {
				selectedShapefiles[fileType].splice(idx, 1);
			}
		}
	}

	function selectShapefile(newFile: File | null, fileType: keyof typeof availableShapefiles) {
		if (newFile) {
			selectedShapefiles[fileType].push(newFile);
			const idx = availableShapefiles[fileType].indexOf(newFile);
			if (idx >= 0) {
				availableShapefiles[fileType].splice(idx, 1);
			}
		}
	}

	function column_ZonesSHP(): MRT_ColumnDef<Job, unknown> {
		return shapefileColumn("zones", "Zone Files", "Zones");
	}

	function column_PointsSHP(): MRT_ColumnDef<Job, unknown> {
		return shapefileColumn("points", "Points Files", "Centroids");
	}

	function column_BoundarySHP(): MRT_ColumnDef<Job, unknown> {
		return shapefileColumn("boundary", "Boundary Files", "Boundary");
	}

	function column_SubmissionNotes(): MRT_ColumnDef<Job, unknown> {
		return {
			id: "submissionNotes",
			header: "Submission Notes",
			accessorKey: "submissionNotes",
			enableEditing: true,
			muiEditTextFieldProps: ({ cell, row }) => ({
				type: "text",
				required: true,
				disabled: false,
				error: !!validationErrors?.[cell.id],
				helperText: validationErrors?.[cell.id],
				value: row.getValue("submissionNotes"),
				onChange: (event) => {
					const newJobs = [...jobs];
					const newJob = newJobs.find((job) => {
						return job.fieldId === row.original.fieldId;
					});
					if (!newJob) return;
					const index = newJobs.indexOf(newJob);
					newJob.submissionNotes = event.target.value;
					row._valuesCache.submissionNotes = newJob.submissionNotes;
					// newJob.dealIds = [];
					newJobs[index] = newJob;
					setJobs(newJobs);
				},
			}),
		};
	}

	function updateSubRows(row: MRT_Row<Job>, field: keyof Job, newValue: any) {
		if (row.subRows) {
			for (const subRow of row.subRows) {
				const job = subRow._valuesCache;
				if (subRow.original.importGrowerName === row.original.importGrowerName) {
					subRow._valuesCache[field] = newValue;
					subRow._valuesCache.dealIds = [
						(
							findDeal(
								props.allDeals,
								job.importDensity ?? 0,
								job.importDepth,
								job.importTestPackage ?? "",
								job.externalFrequencyOfMicros ?? 0,
							)?.id || "custom"
						).toString(),
					];
				}
			}
		}
	}

	function column_FrequencyOfMicros(): MRT_ColumnDef<Job, unknown> {
		return {
			id: "frequencyOfMicros",
			header: "Micros",
			accessorKey: "externalFrequencyOfMicros",
			enableEditing: false,
			size: 100,
			Cell: ({ cell, row }) => {
				return (
					<Autocomplete
						size="small"
						freeSolo
						style={{ width: "100%" }}
						id="grid-size-autocomplete"
						value={jobs.find((job) => job.id === row.original.id)?.externalFrequencyOfMicros || ""}
						options={[
							...new Set(
								props.allDeals.flatMap((deal) => {
									return deal.frequencyOfMicros ?? 0;
								}),
							),
						]}
						getOptionKey={(frequency) => frequency?.toString() || ""}
						getOptionLabel={(frequency) => {
							return frequency.toString();
						}}
						renderInput={(params) => (
							<TextField
								label="Frequency of Micros"
								variant="outlined"
								error={!!validationErrors?.[cell.id]}
								helperText={validationErrors?.[cell.id]}
								required
								{...params}
							/>
						)}
						onBlur={(event) => {
							const newValue = (event.target as HTMLInputElement).value;
							if (newValue != "" && isNaN(parseFloat(newValue.toString()))) {
								setValidationErrors((prev) => ({ ...prev, [cell.id]: "Frequency of Micros must be a number" }));
								return;
							} else {
								setValidationErrors((prev) => {
									const newErrors = { ...prev };
									delete newErrors[cell.id];
									return newErrors;
								});
							}
							const newJobs = [...jobs];
							const newJob = newJobs.find((job) => {
								return job.id === row.original.id;
							});
							if (!newJob) return;
							const index = newJobs.indexOf(newJob);
							newJob.externalFrequencyOfMicros = parseFloat((event.target as HTMLInputElement).value);
							row._valuesCache.externalFrequencyOfMicros = newJob.externalFrequencyOfMicros;
							newJob.dealIds = [
								(
									findDeal(
										props.allDeals,
										newJob.importDensity ?? 0,
										newJob.importDepth ?? 0,
										newJob.externalTestPackageId ?? "",
										newJob.externalFrequencyOfMicros ?? 0,
									)?.id || "custom"
								).toString(),
							];
							row._valuesCache.dealIds = newJob.dealIds;
							newJobs[index] = newJob;
							setJobs(newJobs);
						}}
					/>
				);
			},
			AggregatedCell: ({ row }) => {
				return (
					<Autocomplete
						freeSolo
						style={{ width: "100%" }}
						id="grid-size-autocomplete"
						value={null}
						options={micros}
						getOptionLabel={(frequency) => {
							return frequency.toString();
						}}
						renderInput={(params) => <TextField label="Frequency of Micros" variant="outlined" {...params} />}
						onBlur={(event) => {
							const newValue = (event.target as HTMLInputElement).value;
							if (newValue != "" && isNaN(parseFloat(newValue.toString()))) {
								return;
							}
							const newJobs = [...jobs];
							for (const job of newJobs) {
								if (job.importGrowerName === row.original.importGrowerName) {
									if (newValue !== null) {
										job.externalFrequencyOfMicros = parseFloat(newValue.toString());
									}
									job.dealIds = [
										(
											findDeal(
												props.allDeals,
												job.importDensity ?? 0,
												job.importDepth ?? 0,
												job.externalTestPackageId ?? "",
												job.externalFrequencyOfMicros ?? 0,
											)?.id || "custom"
										).toString(),
									];
								}
							}

							updateSubRows(row, "externalFrequencyOfMicros", parseFloat(newValue.toString()));
							setJobs(newJobs);
						}}
					/>
				);
			},
		};
	}

	function column_TestPackage(): MRT_ColumnDef<Job, unknown> {
		return {
			id: "testPackage",
			header: "Lab Package",
			accessorKey: "externalTestPackageId",
			size: 220,
			enableEditing: false,
			Cell: ({ cell, row }) => {
				return (
					<Autocomplete
						size="small"
						freeSolo
						style={{ width: "100%" }}
						id="grid-size-autocomplete"
						value={jobs.find((job) => job.id === row.original.id)?.externalTestPackageId || ""}
						options={labPackages}
						getOptionKey={(option) => option || ""}
						renderInput={(params) => <TextField label="Lab Package" variant="outlined" {...params} />}
						onBlur={(event) => {
							const newValue = (event.target as HTMLInputElement).value;

							const newJobs = [...jobs];
							const newJob = newJobs.find((job) => {
								return job.id === row.original.id;
							});
							if (!newJob) return;
							const index = newJobs.indexOf(newJob);
							newJob.externalTestPackageId = (event.target as HTMLInputElement).value;
							row._valuesCache.labPackage = newJob.externalTestPackageId;
							newJob.dealIds = [
								(
									findDeal(
										props.allDeals,
										newJob.importDensity ?? 0,
										newJob.importDepth ?? 0,
										newJob.externalTestPackageId ?? "",
										newJob.externalFrequencyOfMicros ?? 0,
									)?.id || "custom"
								).toString(),
							];
							row._valuesCache.dealIds = newJob.dealIds;
							newJobs[index] = newJob;
							setJobs(newJobs);
						}}
					/>
				);
			},
			AggregatedCell: ({ row }) => {
				return (
					<Autocomplete
						freeSolo
						style={{ width: "100%" }}
						id="lab-package-autocomplete"
						value={null}
						options={labPackages}
						getOptionKey={(option) => option || ""}
						renderInput={(params) => <TextField label="Lab Package" variant="outlined" {...params} />}
						onChange={(event, newValue) => {
							const newJobs = [...jobs];
							for (const job of newJobs) {
								if (job.importGrowerName === row.original.importGrowerName) {
									job.externalTestPackageId = Array.isArray(newValue)
										? newValue.join("")
										: (newValue ?? "No test package specified"); // Add null check here
									job.dealIds = [
										(
											findDeal(
												props.allDeals,
												job.importDensity ?? 0,
												job.importDepth ?? 0,
												job.externalTestPackageId ?? "",
												job.externalFrequencyOfMicros ?? 0,
											)?.id || "custom"
										).toString(),
									];
								}
							}
							updateSubRows(row, "externalTestPackageId", newValue);
							setJobs(newJobs);
						}}
					/>
				);
			},
		};
	}

	function column_Depth(): MRT_ColumnDef<Job, unknown> {
		return {
			id: "depth",
			header: "Depth",
			accessorKey: "importDepth",
			size: 100,
			enableEditing: false,
			Cell: ({ cell, row }) => {
				return (
					<Autocomplete
						size="small"
						freeSolo
						style={{ width: "100%" }}
						id="grid-size-autocomplete"
						value={jobs.find((job) => job.id === row.original.id)?.importDepth?.toString() || ""}
						options={depths}
						getOptionKey={(option) => option?.toString() || ""}
						renderInput={(params) => (
							<TextField
								label="Depth"
								variant="outlined"
								error={!!validationErrors?.[cell.id]}
								helperText={validationErrors?.[cell.id]}
								required
								{...params}
							/>
						)}
						onBlur={(event) => {
							const newValue = (event.target as HTMLInputElement).value;
							if (newValue == null || newValue == "" || isNaN(parseFloat(newValue.toString()))) {
								setValidationErrors((prev) => ({ ...prev, [cell.id]: "Depth must be a number" }));
								// console.log("validationErrors", validationErrors);
								return;
							} else {
								setValidationErrors((prev) => {
									const newErrors = { ...prev };
									delete newErrors[cell.id];
									return newErrors;
								});
							}
							const newJobs = [...jobs];
							const newJob = newJobs.find((job) => {
								return job.id === row.original.id;
							});
							if (!newJob) return;
							const index = newJobs.indexOf(newJob);
							newJob.importDepth = parseFloat((event.target as HTMLInputElement).value);
							row._valuesCache.importDepth = newJob.importDepth;
							newJob.dealIds = [
								(
									findDeal(
										props.allDeals,
										newJob.importDensity ?? 0,
										newJob.importDepth,
										newJob.externalTestPackageId ?? "",
										newJob.externalFrequencyOfMicros ?? 0,
									)?.id || ""
								).toString(),
							];
							row._valuesCache.dealIds = newJob.dealIds;
							newJobs[index] = newJob;
							setJobs(newJobs);

							checkErrors(newJobs);
						}}
						getOptionLabel={(depth) => {
							return depth.toString();
						}}
					/>
				);
			},
			AggregatedCell: ({ row }) => {
				return (
					<Autocomplete
						freeSolo
						style={{ width: "100%" }}
						id="grid-size-autocomplete-aggregate"
						value={null}
						options={depths}
						renderInput={(params) => <TextField label="Depth" variant="outlined" {...params} />}
						onBlur={(event) => {
							const newValue = parseFloat((event.target as HTMLInputElement).value);
							if (isNaN(newValue)) {
								return;
							}
							const newJobs = [...jobs];
							for (const job of newJobs) {
								if (job.importGrowerName === row.original.importGrowerName) {
									job.importDepth = parseFloat(newValue.toString());
									job.dealIds = [
										(
											findDeal(
												props.allDeals,
												job.importDensity ?? 0,
												job.importDepth,
												job.externalTestPackageId ?? "",
												job.externalFrequencyOfMicros ?? 0,
											)?.id || "custom"
										).toString(),
									];
								}
							}

							updateSubRows(row, "importDepth", newValue);
							setJobs(newJobs);
							checkErrors(newJobs);
						}}
					/>
				);
			},
		};
	}

	function column_Density(): MRT_ColumnDef<Job, unknown> {
		return {
			id: "density",
			header: "Density",
			accessorKey: "importDensity",
			enableEditing: false,
			size: 160,
			Cell: ({ cell, row }) => {
				return (
					<Autocomplete
						size="small"
						freeSolo
						style={{ width: "100%" }}
						id="density-autocomplete"
						value={jobs.find((job) => job.id === row.original.id)?.importDensity || ""}
						options={gridSizes}
						renderInput={(params) => (
							<TextField
								label="Acre density"
								variant="outlined"
								error={!!validationErrors?.[cell.id]}
								helperText={validationErrors?.[cell.id]}
								required
								{...params}
							/>
						)}
						onBlur={(event) => {
							const newValue = (event.target as HTMLInputElement).value;

							const newJobs = [...jobs];
							const newJob = newJobs.find((job) => {
								return job.id === row.original.id;
							});
							if (!newJob) return;
							const index = newJobs.indexOf(newJob);
							newJob.importDensity = newValue ? parseFloat(newValue.toString()) : undefined;
							row._valuesCache.importDensity = newJob.importDensity;
							newJob.dealIds = [
								(
									findDeal(
										props.allDeals,
										newJob.importDensity ?? 0,
										newJob.importDepth ?? 0,
										newJob.externalTestPackageId ?? "",
										newJob.externalFrequencyOfMicros ?? 0,
									)?.id || "custom"
								).toString(),
							];
							row._valuesCache.dealIds = newJob.dealIds;
							newJobs[index] = newJob;
							setJobs(newJobs);

							checkErrors(newJobs);
						}}
						getOptionLabel={(gridSize) => {
							return gridSize.toString();
						}}
					/>
				);
			},
			AggregatedCell: ({ row }) => {
				return (
					<Autocomplete
						freeSolo
						style={{ width: "100%" }}
						id="grid-size-autocomplete"
						value={undefined}
						options={gridSizes}
						renderInput={(params) => <TextField label="Acre density" variant="outlined" {...params} />}
						onChange={(event, newValue) => {
							const newJobs = [...jobs];
							for (const job of newJobs) {
								if (job.importGrowerName === row.original.importGrowerName) {
									job.importDensity = newValue ? parseFloat(newValue.toString()) : undefined;
									job.dealIds = [
										(
											findDeal(
												props.allDeals,
												job.importDensity ?? 0,
												job.importDepth ?? 0,
												job.externalTestPackageId ?? "",
												job.externalFrequencyOfMicros ?? 0,
											)?.id || "custom"
										).toString(),
									];
								}
							}

							updateSubRows(row, "importDensity", newValue);
							setJobs(newJobs);
							checkErrors(newJobs);
						}}
						getOptionLabel={(gridSize) => {
							return gridSize.toString();
						}}
					/>
				);
			},
		};
	}

	function column_Deal(): MRT_ColumnDef<Job, unknown> {
		return {
			id: "deal",
			header: "Configuration",
			accessorKey: "deal",
			enableEditing: false,
			size: 400,
			Cell: ({ cell, row }) => {
				return (
					<Autocomplete
						fullWidth={true}
						size="small"
						value={
							props.allDeals.find((deal) => deal.id === jobs.find((job) => job.id === row.original.id)?.dealIds[0]) || {
								id: "custom",
							}
						}
						options={[...props.allDeals, { id: "custom" }]}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						renderInput={(params) => (
							<TextField
								error={!!validationErrors?.[row.original.id]}
								helperText={validationErrors?.[row.original.id]}
								{...params}
							/>
						)}
						onChange={(event, newDeal) => {
							const newJobs = [...jobs];
							const newJob = newJobs.find((job) => {
								return job.id === row.original.id;
							});
							if (!newJob) return;
							const index = newJobs.indexOf(newJob);
							newJob.dealIds = [newDeal?.id || "custom"];
							row._valuesCache.dealIds = newJob.dealIds;
							if (newJob.dealIds.at(0) !== "custom") {
								const d = newDeal as Deal;
								newJob.importDensity = d.density;
								row._valuesCache.importDensity = newJob.importDensity;
								newJob.importDepth = d.depth;
								row._valuesCache.importDepth = newJob.importDepth;
								const testPackage = d.testPackage || "No test package specified";
								newJob.externalTestPackageId = Array.isArray(testPackage) ? testPackage.join("") : testPackage;
								row._valuesCache.importTestPackage = newJob.externalTestPackageId;
								newJob.externalFrequencyOfMicros = d.frequencyOfMicros ?? 0;
								row._valuesCache.externalFrequencyOfMicros = newJob.externalFrequencyOfMicros;
							}
							newJobs[index] = newJob;
							setJobs(newJobs);
						}}
						getOptionLabel={(deal) => {
							if (deal && deal.id === "custom") {
								return "No linked Configuration";
							} else {
								return getDealLabel(deal as Deal);
							}
						}}
					/>
				);
			},
			AggregatedCell: ({ row }) => {
				return (
					<>
						<a style={{ fontWeight: "bold", fontSize: "1.2em" }}>Update Group: &nbsp;</a>
						<Autocomplete
							fullWidth={true}
							value={undefined}
							options={[...props.allDeals, { id: "custom" }]}
							renderInput={(params) => <TextField {...params} />}
							onChange={(event, newDeal) => {
								const dealId = newDeal?.id || "custom";
								const newJobs = [...jobs];
								const d = newDeal as Deal;

								for (const job of newJobs) {
									if (job.importGrowerName === row.original.importGrowerName) {
										job.dealIds = [dealId];
										if (dealId != "custom") {
											job.importDensity = d.density;
											job.importDepth = d.depth;
											job.externalTestPackageId = d.testPackage || "No test package specified";
											job.externalFrequencyOfMicros = d.frequencyOfMicros ?? 0;
										}
									}
								}

								if (row.subRows) {
									for (const subRow of row.subRows) {
										if (subRow.original.importGrowerName === row.original.importGrowerName) {
											subRow._valuesCache.dealIds = [dealId];
											subRow._valuesCache.importDensity = d.density;
											subRow._valuesCache.importDepth = d.depth;
											subRow._valuesCache.importTestPackage = d.testPackage || "No test package specified";
											subRow._valuesCache.externalFrequencyOfMicros = d.frequencyOfMicros ?? 0;
										}
									}
								}
								setJobs(newJobs);
							}}
							getOptionLabel={(option) => {
								if (option && option.id === "custom") {
									return "Unlink All Configurations for Group";
								} else {
									const d = option as Deal;
									const dealLabelFields = [d.density, d.type, d.depth, d.testPackage || "No test package specified"];
									if (d.specialTag) dealLabelFields.push(d.specialTag);
									dealLabelFields.push(d.season);
									return `${d.density} Acre ${dealLabelFields.join(" - ")}`;
								}
							}}
						/>
					</>
				);
			},
		};
	}

	function column_LabSubmissionCode(): MRT_ColumnDef<Job, unknown> {
		return {
			id: "labSubmissionCode",
			header: "Lab Submission Code",
			accessorKey: "labSubmissionCode",
			enableEditing: true,
		};
	}

	function column_FieldId(): MRT_ColumnDef<Job, unknown> {
		return {
			id: "fieldId",
			header: "Field ID (Field Key)",
			accessorKey: "fieldId",
		};
	}

	function column_FieldName(): MRT_ColumnDef<Job, unknown> {
		return {
			accessorKey: "importFieldName", //accessorKey used to define `data` column. `id` gets set to accessorKey automatically
			id: "field",
			header: "Field",
			enableEditing: true,
			aggregationFn: "count",
			AggregatedCell: ({ cell }) => {
				return (
					<Box sx={{ backgroundColor: "#d4d5d6", fontWeight: "bold", padding: "4px", borderRadius: "4px" }}>
						# of Fields: {cell.getValue() as string}
					</Box>
				);
			},
			muiEditTextFieldProps: requiredFieldProps("field", "importFieldName", "Field name is required"),
		};
	}

	function column_Farm(): MRT_ColumnDef<Job, unknown> {
		return {
			accessorKey: "importFarmName", //accessorKey used to define `data` column. `id` gets set to accessorKey automatically
			id: "farm",
			header: "Farm",
			enableEditing: true,
			muiEditTextFieldProps: requiredFieldProps("farm", "importFarmName", "Farm name is required"),
		};
	}

	function column_Grower(): MRT_ColumnDef<Job, unknown> {
		return {
			accessorKey: "importGrowerName", //accessorKey used to define `data` column. `id` gets set to accessorKey automatically
			id: "grower",
			header: "Grower",
			enableEditing: true,
			muiEditTextFieldProps: requiredFieldProps("grower", "importGrowerName", "Grower name is required"),
		};
	}
	//#endregion

	//#region TABLE
	function allJobsHaveNoFiles(jobs: Job[]) {
		for (const job of jobs) {
			if (job.files.boundarySHP && job.files.boundarySHP.length > 0) return false;
			if (job.files.zonesSHP && job.files.zonesSHP.length > 0) return false;
			if (job.files.pointsSHP && job.files.pointsSHP.length > 0) return false;
		}
		return true;
	}

	const table = useMaterialReactTable({
		columns,
		data: jobs, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
		enableColumnFilterModes: true,
		enableColumnOrdering: true,
		enableGrouping: true,
		enableColumnPinning: true,
		enableColumnResizing: true,
		enableColumnActions: true,
		enableColumnDragging: false,
		enableCellActions: true,
		enableRowSelection: !!props.allowMap,
		enableEditing: true,
		editDisplayMode: "table", // ('modal', 'row', 'cell', 'table', and 'custom' are also
		onCreatingRowCancel: () => setValidationErrors({}),
		onEditingRowCancel: () => setValidationErrors({}),
		groupedColumnMode: "remove",
		muiTableProps: {
			sx: { maxHeight: "90vh" },
		},
		onExpandedChange: (isExpanded) => {
			setExpanded(isExpanded);
		},
		enableStickyFooter: true,
		state: {
			rowSelection,
			columnVisibility,
			grouping,
			expanded: expanded,
			density: "compact",
			pagination,
		},
		onPaginationChange: (pagination) => {
			setPagination(pagination);
		},
		onRowSelectionChange: (rows) => {
			const newRowSelection = typeof rows === "function" ? rows(rowSelection) : rows;
			props.setDrawerState(Object.keys(newRowSelection).length > 0);
			setRowSelection(newRowSelection);

			// Automatically update the map when row selection changes
			if (Object.keys(newRowSelection).length > 0 && props.setDisplayBoundaries) {
				setTimeout(async () => {
					await updateBoundaries(newRowSelection);
				}, 100);
			}
		},
		displayColumnDefOptions: {
			"mrt-row-select": {
				Header: ({ table }) => (
					<Stack direction="row" alignItems="center" spacing={1} color={"primary"}>
						<label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
							<MRT_SelectCheckbox table={table} />
							<Box ml={0.5}>
								<Map />
							</Box>
						</label>
					</Stack>
				),
				Cell: ({ row, table }) => (
					<Stack direction="row" alignItems="center" spacing={1} color={"primary"}>
						<label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
							<MRT_SelectCheckbox row={row} table={table} />
							<Box ml={0.5}>
								<Map />
							</Box>
						</label>
					</Stack>
				),
				size: 80,
			},
			"mrt-row-expand": {
				Header: () => (
					<Stack direction="row" alignItems="center" color={"secondary"}>
						<MRT_ExpandAllButton table={table} />
						<Box>Groups</Box>
					</Stack>
				),
				GroupedCell: ({ row, table }) => {
					const { grouping } = table.getState();
					return row.getValue(grouping[grouping.length - 1]);
				},
				enableResizing: true,
				size: 200,
			},
		},
		onColumnVisibilityChange: (column) => {
			setColumnVisibility(column);
		},
		onColumnOrderChange: (columns) => {
			setColumnOrder(columns);
		},
		onGroupingChange: (grouping) => {
			setGrouping(grouping);
		},
		muiTableBodyCellProps: ({ row }) => ({
			sx: {
				backgroundColor: row.depth === 0 ? "#d4d5d6" : "inherit",
			},
		}),
		layoutMode: "grid",
		initialState: {
			showGlobalFilter: true,
			grouping,
			pagination: {
				pageIndex: 0,
				pageSize: 20,
			},
			columnPinning: {
				left: ["grower", "field"],
				right: ["mrt-row-select"],
			},
			columnVisibility: { farm: false },
		},
		paginationDisplayMode: "pages",
		positionToolbarAlertBanner: "bottom",
		muiSearchTextFieldProps: {
			size: "small",
			variant: "outlined",
		},
		muiPaginationProps: {
			color: "secondary",
			rowsPerPageOptions: [10, 15, 20, 30, 50],
			shape: "rounded",
			variant: "outlined",
		},
		renderTopToolbar: ({ table }) => {
			return (
				<Box
					sx={(theme) => ({
						backgroundColor: lighten(theme.palette.background.default, 0.05),
						display: "flex",
						gap: "0.5rem",
						// p: '8px',
						justifyContent: "space-between",
					})}
				>
					<Box sx={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
						{/* import MRT sub-components */}
						<MRT_GlobalFilterTextField table={table} />
						<MRT_ToggleFiltersButton table={table} />
					</Box>
					<Box>
						<Box sx={{ display: "flex", gap: "0.5rem" }}>
							{hasValidationErrors() && (
								<Typography color="error" sx={{ p: "6px" }}>
									Fill out all required fields
								</Typography>
							)}
							{props.user?.isAdmin && (
								<Button color="primary" onClick={downloadBulkImport}>
									<Download className="icon-in-button" />
									Download Bulk Import
								</Button>
							)}
							{props.shouldShowAutoMatch && (
								<Button color="primary" variant="contained" onClick={autoMatchFiles}>
									<AutoAwesome className="icon-in-button" />
									Auto Match Files
								</Button>
							)}
							{/* {Object.keys(rowSelection).length >= 2 &&
								<Button color="primary" variant="outlined" onClick={mergeSelectedJobs}>
								<CallMerge className="icon-in-button" />
								Merge Selected
								</Button>
							} */}
							{!!props.setDisplayBoundaries && Object.keys(rowSelection).length > 0 && (
								<Tooltip title={getShowMapText()}>
									<Button
										color="primary"
										variant="outlined"
										onClick={showOnMap}
										disabled={
											Object.keys(selectionDisplayedOnMap).length === Object.keys(rowSelection).length &&
											Object.keys(rowSelection).every((key) => !!selectionDisplayedOnMap[key])
										}
									>
										<Map className="icon-in-button" />
										{getShowMapText()}
									</Button>
								</Tooltip>
							)}
							<Button color="primary" variant="contained" onClick={createJobs} disabled={hasValidationErrors()}>
								<Publish className="icon-in-button" />
								Create Jobs
							</Button>
							<MRT_ToggleFullScreenButton table={table} />
						</Box>
					</Box>
				</Box>
			);
		},
	});
	function hasValidationErrors() {
		return Object.values(validationErrors).some((error) => !!error);
	}

	function validateRequiredFields(): boolean {
		let hasErrors = false;

		for (const job of jobs) {
			// Check grower name
			if (!job.importGrowerName || job.importGrowerName.trim() === "") {
				setValidationErrors((prev) => ({ ...prev, [`grower-${job.id}`]: "Grower name is required" }));
				hasErrors = true;
			}

			// Check farm name
			if (!job.importFarmName || job.importFarmName.trim() === "") {
				setValidationErrors((prev) => ({ ...prev, [`farm-${job.id}`]: "Farm name is required" }));
				hasErrors = true;
			}

			// Check field name
			if (!job.importFieldName || job.importFieldName.trim() === "") {
				setValidationErrors((prev) => ({ ...prev, [`field-${job.id}`]: "Field name is required" }));
				hasErrors = true;
			}
		}

		return !hasErrors;
	}

	async function mergeSelectedJobs() {
		if (selectedJobs.length < 2) {
			toast.error("Please select at least 2 jobs to merge");
			return;
		}

		const sortedSelectedJobs = sortSelectedJobs();
		const firstJob = sortedSelectedJobs[0];

		// const invalidFields: string[] = checkForInvalidFields();
		// if (invalidFields.length > 0) {
		//   toast.error(`Cannot merge jobs with different values for: ${invalidFields.join(', ')}`);
		//   return;
		// }

		const bndShp = await mergeFiles("boundarySHP");
		const ptsShp = await mergeFiles("pointsSHP");
		const znsSHP = await mergeFiles("zonesSHP");
		const mergedJob: Job = {
			...firstJob,
			files: {
				boundarySHP: bndShp,
				pointsSHP: ptsShp,
				zonesSHP: znsSHP,
			},
		};

		updateShapefileList();

		// Remove selected jobs from the jobs array
		const selectedJobIds = new Set(sortedSelectedJobs.map((job) => job.id));
		const updatedJobs = jobs.filter((job) => !selectedJobIds.has(job.id));

		// Insert the merged job at the position of the first selected job
		updatedJobs.splice(calculateNewIndex(), 0, mergedJob);

		setJobs(updatedJobs);
		setRowSelection({});
		toast.success(`Successfully merged ${selectedJobs.length}`);

		function updateShapefileList() {
			for (const fileType of ["boundarySHP", "pointsSHP", "zonesSHP"] as const) {
				const oldFiles = sortedSelectedJobs.flatMap((job) => job.files[fileType] ?? []);
				const geoFileType = fileType === "boundarySHP" ? "Boundary" : fileType === "pointsSHP" ? "Centroids" : "Zones";
				if (allShapefiles[geoFileType]) {
					allShapefiles[geoFileType] = allShapefiles[geoFileType].filter((file) => !oldFiles.includes(file));
					allShapefiles[geoFileType].push(...(mergedJob.files[fileType] || []));
				}
			}
			setAllShapefiles({ ...allShapefiles });
		}

		async function mergeFiles(fileType: "zonesSHP" | "pointsSHP" | "boundarySHP"): Promise<File[]> {
			const geoJsons = await Promise.all(
				sortedSelectedJobs.flatMap((job) => (job.files[fileType] ? GeoData.toGeoJSON(job.files[fileType], "SHP") : [])),
			);
			const mergedGeoJson = GeoData.merge(geoJsons);
			if (!mergedGeoJson?.features?.length) {
				return [];
			}
			const shpFile = await GeoData.toSHP_File(
				mergedGeoJson,
				"GeoJSON",
				firstJob.files[fileType]?.[0]?.name || fileType,
				firstJob.fieldName,
			);
			return [shpFile];
		}

		function sortSelectedJobs(): Job[] {
			// Sort selected jobs by their original position in the jobs array
			return selectedJobs.sort((a, b) => {
				const indexA = jobs.findIndex((job) => job.id === a.id);
				const indexB = jobs.findIndex((job) => job.id === b.id);
				return indexA - indexB;
			});
		}

		// function checkForInvalidFields(): string[] {
		//   const incompatibleFields: string[] = [];

		//   // Check if key fields are identical across all selected jobs
		//   const fieldsToCheck = [
		//     'importGrowerName',
		//     'importFarmName',
		//     'importFieldName',
		//   ] as const;

		//   fieldsToCheck.forEach(field => {
		//     const firstValue = firstJob[field];
		//     const allMatch = sortedSelectedJobs.every(job => job[field] === firstValue);
		//     if (!allMatch) {
		//       switch (field) {
		//         case 'importGrowerName':
		//           incompatibleFields.push('Grower');
		//           break;
		//         case 'importFarmName':
		//           incompatibleFields.push('Farm');
		//           break;
		//         case 'importFieldName':
		//           incompatibleFields.push('Field');
		//           break;
		//       }
		//     }
		//   });
		//   return incompatibleFields;
		// }

		function calculateNewIndex(): number {
			const firstSelectedIndex = jobs.findIndex((job) => job.id === firstJob.id);
			let adjustedIndex = firstSelectedIndex;
			for (let i = 0; i < firstSelectedIndex; i++) {
				if (selectedJobIds.has(jobs[i].id)) {
					adjustedIndex--;
				}
			}
			return adjustedIndex;
		}
	}

	//#endregion

	//#region FUNCTIONS
	async function createJobs() {
		props.setLoading("submit");

		// First check existing validation errors
		if (validationErrors && Object.keys(validationErrors).length > 0) {
			toast.error("Fill out required fields");
			props.setLoading(false);
			return;
		}

		// Then validate all required fields
		if (!validateRequiredFields()) {
			toast.error("Fill out required fields");
			props.setLoading(false);
			return;
		}

		if (allJobsHaveNoFiles(jobs)) {
			if (!window.confirm("No shapefiles attached. Proceed?")) {
				return;
			}
		}

		const creating = toast.loading(`Creating ${jobs.length} Jobs...`);

		jobs.forEach((job) => {
			if (!job.jobFlags.includes("Bulk Upload")) job.jobFlags.push("Bulk Upload");
			job.dealIds = job.dealIds[0] === "custom" ? [] : job.dealIds;
		});

		try {
			let source: JobFlags;
			switch (props.integration) {
				case "Proagrica":
					source = "Created Portal API Proagrica";
					break;
				case "Agrian":
					source = "Created Portal API Agrian";
					break;
				default:
					source = "Created Portal Bulk";
					break;
			}
			const createdJobs = await API.Data.Jobs.create.many(jobs, source);
			toast.success(`Created ${jobs.length} Jobs! Please give 1-2 business days for jobs to show up in the dashboard.`);
			props.resetForm();
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.JobImportTable.createJobs");
		} finally {
			toast.dismiss(creating);
			props.setLoading(false);
		}
	}

	async function downloadBulkImport() {
		const csv = generateBulkUploadCSV(jobs);

		const zip = new JSZip();
		zip.file("jobs.csv", csv);

		for (const job of jobs) {
			const files: File[] = [];
			if (job.files.boundarySHP) files.push(...Array.from(job.files.boundarySHP));
			if (job.files.zonesSHP) files.push(...Array.from(job.files.zonesSHP));
			if (job.files.pointsSHP) files.push(...Array.from(job.files.pointsSHP));
			if (job.files.fieldNotes) files.push(...Array.from(job.files.fieldNotes));

			const jobZip = zip.folder((job.externalId ?? job.importFieldName) || job.id);
			for (const file of files) {
				jobZip?.file(file.name, file);
			}
		}

		return await FileDownloader.downloadZip(zip, "bulk_upload");

		function generateBulkUploadCSV(jobs: Job[]) {
			const csvRows = [
				"3rd Party Reference ID,3rd Party Lab Test Package ID,Boundary Change Type,Branch / Location #form,Depth from Form,Deal,Event ID,Farm from Form,Field from Form,Field ID,Field Notes,Field Ready at Submission?,Grower Cell Phone Number,Grower from Form,Grower Link,Lab from Form,Lab Instructions,Priority OVRD,Regular Lab Submission Code,Retest? (Pts Submitted by Customer),Submission Notes,Submitter Email,Submitter,Job Flags",
			];
			for (const job of jobs) {
				const row = [
					job.externalId,
					job.externalTestPackageId,
					job.boundaryChangeType ? "Major" : "",
					job.branchIds[0],
					job.importDepth,
					job.dealIds[0],
					job.eventId,
					job.importFarmName,
					job.importFieldName,
					job.fieldId,
					job.files.fieldNotes ? job.files.fieldNotes[0]?.name : "",
					job.isReadyToSample,
					job.growerPhoneNumber,
					job.importGrowerName,
					job.growerIds[0],
					job.importLabName,
					job.labInstructions,
					job.fieldPriority,
					job.labSubmissionCode,
					job.isPointsAttached,
					job.submissionNotes,
					job.submitterEmail,
					job.submitterName,
					job.jobFlags,
				].join(",");

				csvRows.push(row);
			}

			const csv = csvRows.join("\n");
			return csv;
		}
	}

	function autoMatchFiles() {
		const startingFileCount = {
			boundarySHP: allShapefiles?.Boundary?.length ?? 0,
			pointsSHP: allShapefiles?.Centroids?.length ?? 0,
			zonesSHP: allShapefiles?.Zones?.length ?? 0,
		};

		const identified = {
			boundarySHP: 0,
			pointsSHP: 0,
			zonesSHP: 0,
		};

		const allFieldNamesUnique = getAllFieldNamesUnique();
		const allFieldIdsUnique = getAllFieldIdsUnique();

		for (const job of jobs) {
			job.files.boundarySHP = [];
			job.files.pointsSHP = [];
			job.files.zonesSHP = [];
		}

		let unmatchedFiles = allShapefiles;
		for (let i = 0; i < 5; i++) {
			unmatchedFiles = matchFiles(unmatchedFiles, i);
		}

		notifyUser();

		for (const job of jobs) {
			removeFilesFromAvailable(job.files.boundarySHP!, "Boundary");
			removeFilesFromAvailable(job.files.pointsSHP!, "Centroids");
			removeFilesFromAvailable(job.files.zonesSHP!, "Zones");
		}

		setAvailableShapefiles({ ...availableShapefiles });
		props.updateJobs([...jobs]);

		function matchFiles(files: { Boundary: File[]; Centroids: File[]; Cores: File[]; Zones: File[] }, i: number = 0) {
			const matchedFiles = {
				Boundary: [] as File[],
				Zones: [] as File[],
				Centroids: [] as File[],
				Cores: [] as File[],
				Unknown: [] as File[],
			};

			if (thereAreStillFilesToMatch()) {
				for (const job of jobs) {
					if (files.Boundary.length > 0 && job.files.boundarySHP?.length === 0) {
						matchedFiles.Boundary.push(...matchFilesForJob(job, "boundarySHP", files.Boundary));
					}
					if (files.Centroids.length > 0 && job.files.pointsSHP?.length === 0) {
						matchedFiles.Centroids.push(...matchFilesForJob(job, "pointsSHP", files.Centroids));
					}
					if (files.Zones.length > 0 && job.files.zonesSHP?.length === 0) {
						matchedFiles.Zones.push(...matchFilesForJob(job, "zonesSHP", files.Zones));
					}
				}
			}

			const unmatchedFiles = {
				Boundary: files.Boundary.filter((file) => !matchedFiles.Boundary.includes(file)),
				Centroids: files.Centroids.filter((file) => !matchedFiles.Centroids.includes(file)),
				Cores: [],
				Zones: files.Zones.filter((file) => !matchedFiles.Zones.includes(file)),
				Unknown: [] as File[],
			};
			return unmatchedFiles;

			function thereAreStillFilesToMatch() {
				return (
					(files.Boundary.length > 0 && files.Boundary.length + jobs.length > startingFileCount.boundarySHP) ||
					(files.Centroids.length > 0 && files.Centroids.length + jobs.length > startingFileCount.pointsSHP) ||
					(files.Zones.length > 0 && files.Zones.length + jobs.length > startingFileCount.zonesSHP)
				);
			}

			function matchFilesForJob(job: Job, type: "boundarySHP" | "pointsSHP" | "zonesSHP", files: File[]): File[] {
				let matchedFileCount = 0;
				for (const file of files) {
					matchedFileCount += matchFile(file) ? 1 : 0;
				}

				const matchedFiles: File[] = [];

				if (matchedFileCount == 1) {
					for (const file of files) {
						const fileMatches = matchFile(file);

						if (fileMatches) {
							matchedFiles.push(file);
							identified[type]++;
							job.files[type] = [file];
						}
					}
				}

				return matchedFiles;

				function matchFile(file: File) {
					const filename = cleanString(file.name);

					const grower = cleanString(job.importGrowerName ?? "");
					const farm = cleanString(job.importFarmName ?? "");
					const field = cleanString(job.importFieldName ?? "");

					const fieldIdMatches = job.fieldId ? filename.includes(job.fieldId) : false;
					const fieldNameMatches = filename.includes(field);
					const growerMatches = filename.includes(grower);
					const farmMatches = filename.includes(farm);

					let fileMatches = false;

					if (allFieldIdsUnique) {
						fileMatches = fieldIdMatches;
					} else if (allFieldNamesUnique) {
						fileMatches = fieldNameMatches;
					} else {
						fileMatches = fieldNameMatches && growerMatches && (!farm || farmMatches);
					}

					return fileMatches;
				}
			}
		}

		function notifyUser() {
			const addFieldKey = "Please try adding the Field Key/ID to the file names.";
			const identifiedZoneFiles = `Identified ${identified.pointsSHP} zones files for ${jobs.length} jobs.`;
			if (allShapefiles.Zones.length > 0) {
				toast[identified.pointsSHP === 0 ? "error" : "info"](
					`${identifiedZoneFiles} ${identified.pointsSHP === 0 ? addFieldKey : ""}`,
				);
			}

			const identifiedPointFiles = `Identified ${identified.pointsSHP} points files for ${jobs.length} jobs.`;
			if (allShapefiles.Centroids.length > 0) {
				toast[identified.pointsSHP === 0 ? "error" : "info"](
					`${identifiedPointFiles} ${identified.pointsSHP === 0 ? addFieldKey : ""}`,
				);
			}

			const identifiedBoundaryFiles = `Identified ${identified.boundarySHP} boundary files for ${jobs.length} jobs.`;
			if (allShapefiles.Boundary.length > 0) {
				toast[identified.boundarySHP === 0 ? "error" : "info"](
					`${identifiedBoundaryFiles} ${identified.boundarySHP === 0 ? addFieldKey : ""}`,
				);
			}
		}

		function removeFilesFromAvailable(files: File[], type: "Zones" | "Centroids" | "Boundary") {
			for (const file of files) {
				selectShapefile(file, type);
			}
		}

		function cleanString(str: string) {
			return str.toLowerCase().replace(/ /g, "").replace(/,/g, "").replace(/#/g, "");
		}

		function getAllFieldNamesUnique() {
			const fieldNames = jobs.map((job) => job.importFieldName ?? "");
			const uniqueFieldNames = new Set(fieldNames);
			const _allFieldNamesUnique = fieldNames.length === uniqueFieldNames.size;
			let allBoundariesMatchUniquely = false;
			if (_allFieldNamesUnique) {
				allBoundariesMatchUniquely = allStringsMatchUniquely(
					allShapefiles["Boundary"]?.map((file) => file.name),
					fieldNames,
				);
			}
			return _allFieldNamesUnique;
		}

		function getAllFieldIdsUnique() {
			const fieldIds = jobs.map((job) => job.fieldId ?? "");
			const uniqueFieldIds = new Set(fieldIds);
			const _allFieldIdsUnique = fieldIds.length === uniqueFieldIds.size;
			let allBoundariesMatchUniquely = false;
			if (_allFieldIdsUnique) {
				allBoundariesMatchUniquely = allStringsMatchUniquely(
					allShapefiles["Boundary"]?.map((file) => file.name),
					fieldIds,
				);
			}
			return _allFieldIdsUnique;
		}
	}

	return (
		<div id="bulk-import-table">
			<MaterialReactTable table={table} />
		</div>
	);
}

export function allStringsMatchUniquely(strings: string[], substrings: string[]) {
	for (const searchString of substrings) {
		let count = 0;
		for (const stringToken of strings) {
			if (searchString.includes(stringToken)) {
				count++;
			}
		}
		if (count !== 1) {
			return false;
		}
	}
	return true;
}
